import { useState, useCallback } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';

function sanitizeInput(str) {
  if (typeof str !== 'string') return '';
  return str.trim().replace(/[<>'"]/g, '').slice(0, 500);
}

const formRateLimit = {
  attempts: {},
  maxAttempts: 5,
  windowMs: 60000,
  check(formId) {
    const now = Date.now();
    if (!this.attempts[formId]) this.attempts[formId] = { count: 0, reset: now + this.windowMs };
    if (now > this.attempts[formId].reset) this.attempts[formId] = { count: 0, reset: now + this.windowMs };
    this.attempts[formId].count++;
    return this.attempts[formId].count <= this.maxAttempts;
  }
};

export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('general');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const showError = useCallback((msg) => {
    setError(msg);
    setTimeout(() => setError(''), 4000);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formRateLimit.check('contact')) return showError('Demasiados intentos. Espera 1 minuto.');
    const cleanName = sanitizeInput(name);
    const cleanEmail = sanitizeInput(email);
    const cleanMsg = sanitizeInput(message);
    if (!cleanName || !cleanEmail || !cleanMsg) return showError('Por favor rellena todos los campos obligatorios');
    if (!cleanEmail.includes('@')) return showError('Introduce un email válido');

    try {
      const msgs = JSON.parse(localStorage.getItem('contactMessages') || '[]');
      msgs.push({ name: cleanName, email: cleanEmail, phone: sanitizeInput(phone), subject, message: cleanMsg, timestamp: new Date().toISOString() });
      localStorage.setItem('contactMessages', JSON.stringify(msgs));
    } catch(e) { /* */ }

    setSent(true);
    setName(''); setEmail(''); setPhone(''); setMessage('');
    setTimeout(() => setSent(false), 5000);
  };

  return (
    <div className="contact-page" data-testid="contact-page">
      <div className="contact-page__inner">
        <div className="section-header reveal">
          <div className="gold-line" />
          <span className="section-label">Contact</span>
          <h2 className="section-title" style={{ marginBottom: '0.5rem' }}>Contacto</h2>
          <p style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', maxWidth: '500px' }}>
            Estaremos encantados de atenderte. Escríbenos o llámanos directamente.
          </p>
        </div>

        {error && <div className="booking-toast booking-toast--error">{error}</div>}
        {sent && <div className="booking-toast booking-toast--success" data-testid="contact-success">Mensaje enviado correctamente (demo)</div>}

        <div className="contact-grid">
          {/* Info */}
          <div>
            <div className="contact-info__item reveal stagger-1">
              <span className="contact-info__label"><MapPin size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.4rem', color: 'var(--gold)' }} />Dirección</span>
              <div className="contact-info__value">12 Rue de la Paix<br />75002 Paris, France</div>
            </div>
            <div className="contact-info__item reveal stagger-2">
              <span className="contact-info__label"><Phone size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.4rem', color: 'var(--gold)' }} />Teléfono</span>
              <div className="contact-info__value">
                <a href="tel:+34600000000">+34 600 000 000</a>
              </div>
            </div>
            <div className="contact-info__item reveal stagger-3">
              <span className="contact-info__label"><Mail size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.4rem', color: 'var(--gold)' }} />Email</span>
              <div className="contact-info__value">
                <a href="mailto:info@hotelmaison.com">info@hotelmaison.com</a>
              </div>
            </div>
            <div className="contact-info__item reveal stagger-4">
              <span className="contact-info__label"><Clock size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.4rem', color: 'var(--gold)' }} />Horarios</span>
              <div className="contact-info__value">
                Recepción: 24 horas<br />
                Le Jardin: Ma-Do 20:00–22:30<br />
                Spa: 8:00–22:00
              </div>
            </div>

            <div className="reveal stagger-5" style={{ marginTop: '2rem' }}>
              <a href="https://wa.me/34600000000?text=Hola,%20me%20gustaría%20información%20sobre%20Maison%20Dorée" target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="contact-whatsapp-btn">
                <svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/></svg>
                Contactar por WhatsApp
              </a>
            </div>
          </div>

          {/* Form */}
          <form className="contact-form reveal-right" onSubmit={handleSubmit} data-testid="contact-form">
            <div className="contact-form__row">
              <div className="booking-field">
                <label>Nombre *</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Tu nombre" data-testid="contact-name" />
              </div>
              <div className="booking-field">
                <label>Email *</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@ejemplo.com" data-testid="contact-email" />
              </div>
            </div>
            <div className="contact-form__row">
              <div className="booking-field">
                <label>Teléfono</label>
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+34 600 000 000" data-testid="contact-phone" />
              </div>
              <div className="booking-field">
                <label>Asunto</label>
                <select value={subject} onChange={e => setSubject(e.target.value)} data-testid="contact-subject">
                  <option value="general">Consulta general</option>
                  <option value="reservas">Reservas</option>
                  <option value="restaurante">Restaurante</option>
                  <option value="eventos">Eventos</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>
            <div className="booking-field">
              <label>Mensaje *</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="¿En qué podemos ayudarte?" rows={5} data-testid="contact-message" />
            </div>
            <button type="submit" className="btn-gold" style={{ width: '100%', justifyContent: 'center' }} data-testid="contact-submit">
              <Send size={16} /> Enviar mensaje
            </button>
          </form>
        </div>

        {/* Map placeholder */}
        <div className="contact-map reveal" data-testid="contact-map">
          <div style={{ textAlign: 'center' }}>
            <MapPin size={32} style={{ color: 'var(--gold)', marginBottom: '0.75rem' }} />
            <p>12 Rue de la Paix, 75002 Paris</p>
            <p style={{ fontSize: 'var(--text-xs)', marginTop: '0.5rem', color: 'var(--text-muted)' }}>Demo: Mapa interactivo en producción</p>
          </div>
        </div>
      </div>
    </div>
  );
}
